// Unpublished Work © 2023-2024 Deere & Company.
import { AxiosResponse } from "axios";
import { BlobResponse } from "../../utils/downloadFile";

export interface Address {
  addressLine1?: string;
  addressLine2?: string;
  cityName?: string;
  stateOrProvince?: string;
  countryCode?: string;
  postalOrZipCode?: string;
  phoneNumber?: string;
}

export interface LineItem {
  serialNumber: number;
  invoicedPart: string | null;
  partDesc: string;
  billingQuantity: number;
  timeLimitExceeded: boolean;
}

export interface PalletCharge {
  description: string;
  palletCharge: number;

  unitPrice: number;

  quantity: number;
}

export interface PalletCharges {
  totalPalletCharge: number;
  palletCharges?: PalletCharge[];
}

export interface CanadaTaxDetails {
  canadaGST: number;
  canadaPST: number;
}

export interface Invoice {
  invoiceNumber: string;
  nfeNumber: string;
  timeLimitExceeded: boolean;
  billingDate?: string;
  lineItems: LineItem[];
}

export interface DealerTimeLimitInvoiceLineItem {
  dealerId: string;
  transactionType: string;
  invoiceNumber: string;
  invoiceLineNumber: string;
  returnReason: string;
  partNumber: string;
  timeLimitExceeded: string;
}

export interface InvoiceDocument {
  documentNumber: string;
  dealerReference: string;
  handlingUnit?: string;
  orderType?: string;
}

export interface InvoiceHeader {
  dealerAccount?: string;
  invoiceNumber: string;
  orderType?: string;
  nfeNumber: string;
  factura: string;
  billingDate: string;
  invoiceType: string;
  viewedDate: string;
  viewedBy: string;
  reversed: boolean;
  officialDocumentNumber: string;
  docClass: string;
  transferStatus: string;
  isVendorDirect: boolean;
  documents: InvoiceDocument[];
  originalNfeNumber?: string;
}

export interface ConsolidatedInvoiceExcelItem {
  invoiceNumber: string;
  invoiceType: string;
  nfe: string;
  originalNfe: string;
}

export type ConsolidatedInvoicesExcelRequest = {
  dealerLocation: string;
} & Record<string, ConsolidatedInvoiceExcelItem[] | string>;

export interface InvoicesResponse {
  invoices: InvoiceHeader[];
  pageSize: number;
  pageNumber: number;
  total: number;
  totalViewed: number;
}

export interface TimeLimit {
  claimsExceeded: boolean;
  returnsExceeded: boolean;
}

export interface InvoiceHeaderDetails {
  invoiceNumber: string;
  nfeNumber: string;
  invoiceDate: string;
  invoiceType: string;
  originalInvoiceNum: string;
  originalNfeNumber: string;
  claimNumber: string;
  partsSubtotal: number;
  discounts: number;
  surcharges: number;
  freightCharges: number;
  palletCharges: PalletCharges;
  envFees: number;
  canadaTaxDetails: CanadaTaxDetails;
  flashPlusCharges: number;
  tax: number;
  total: number;
  currency: string;
  orderType?: string | string[];
  shipToAddress: Address;
  soldToAddress: Address;
  shippingInformation: Address;
  carrierDetails: string;
  reversed: boolean;
  officialDocumentNumber: string;
  timeLimit: TimeLimit;
  docClass: string;
  isVendorDirect: boolean;
  attachments: string[];
}

export interface InvoiceHeaderDetailsV2 {
  invoiceNumber: string;
  nfeNumber: string;
  factura: string;
  invoiceDate: string;
  invoiceType: string;
  originalInvoiceNum: string;
  originalNfeNumber: string;
  claimNumber: string;
  partsSubtotal: number;
  discounts: number;
  surcharges: number;
  freightCharges: number;
  palletCharges?: PalletCharges;
  envFees: number;
  canadaTaxDetails: CanadaTaxDetails;
  flashPlusCharges: number;
  tax: number;
  total: number;
  currency: string;
  orderType?: string | string[];
  shipToAddress: Address;
  soldToAddress: Address;
  shippingInformation: Address;
  carrierDetails: string;
  reversed: boolean;
  officialDocumentNumber: string;
  timeLimit: TimeLimit;
  docClass: string;
  isVendorDirect: boolean;
  attachments: string[];
  dealerAccount?: string;
}

export interface HandlingUnit {
  number: string;
  quantity: number;
  quantityUnit: string;
}

export interface InvoiceLineItem {
  lineNumber: number;
  documentNumber: string;
  orderType: string;
  partNumber: string;
  description: string;
  quantity: number;
  unitPrice: number;
  coreUnitPrice: number;
  extendedDiscount: number;
  freightCharges: number;
  flashPlusCharges: number;
  extendedAmount: number;
  corePrice: number;
  deliveryNumber: string;
  dealerReference: string;
  handlingUnits: HandlingUnit[];
  timeLimitExceeded: string;
}

export const EXCEL_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export type IInvoiceApi = {
  getInvoice: (
    omOperationsServiceUrl: string,
    invoiceNumber: string,
    accountId: string,
    token: string,
    location?: string,
    lang?: string,
    lineItemPartDescTranslationEnabled?: boolean
  ) => Promise<Invoice>;
  getInvoiceWithTimeLimit: (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    accountId: string,
    token: string,
    transactionType: string,
    location?: string,
    lang?: string
  ) => Promise<Invoice>;
  getMultiInvoiceTimeLimitForLineItems: (
    invoiceServiceUrl: string,
    accountId: string,
    token: string,
    transactionType: string,
    invoiceNumbers: string
  ) => Promise<DealerTimeLimitInvoiceLineItem[]>;
  downloadInvoices: (
    invoiceServiceUrl: string,
    accountId: string,
    jobId: string,
    token: string
  ) => Promise<AxiosResponse<Blob>>;
  getInvoices: (
    invoiceServiceUrl: string,
    accountId: string,
    token: string,
    lang?: string,
    pageSize?: number,
    pageNumber?: number,
    startDate?: string,
    endDate?: string,
    orderBy?: string,
    sortBy?: string,
    invoiceNumber?: string,
    documentNumber?: string,
    dealerReference?: string,
    invoiceType?: string,
    partNumber?: string,
    huNumber?: string,
    nfeNumber?: string,
    officialDocumentNumber?: string,
    withHandlingUnits?: boolean,
    withLeadingZeros?: boolean,
    dealerIdFilter?: string,
    factura?: string,
    mru?: string,
    country?: string,
    srcProcessType?: string
  ) => Promise<InvoicesResponse>;
  invoiceViewed: (
    invoiceServiceUrl: string,
    invoiceNumber: string | undefined,
    dealerId: string,
    seenBy: string,
    country: string | undefined,
    accessToken: string | undefined,
    lang: string
  ) => Promise<void>;
  getInvoiceDetails: (
    omOperationsServiceUrl: string,
    invoiceNumber: string,
    token: string,
    accountId: string,
    mru: string,
    location?: string,
    lang?: string,
    withLeadingZeros?: boolean,
    portlandSurchargesEnabled?: boolean
  ) => Promise<InvoiceHeaderDetails>;
  getInvoiceDetailsV2: (
    omOperationsServiceUrl: string,
    invoiceNumber: string,
    token: string,
    accountId: string,
    mru: string,
    location?: string,
    lang?: string,
    withLeadingZeros?: boolean,
    portlandSurchargesEnabled?: boolean
  ) => Promise<InvoiceHeaderDetailsV2>;
  getInvoiceLineItems: (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    token: string,
    location?: string,
    lang?: string,
    accountId?: string,
    isInvoiceLineItemsPartDescTranslation?: boolean,
    portlandSurchargesEnabled?: boolean
  ) => Promise<InvoiceLineItem[]>;
  getInvoicePDF: (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    accountId: string,
    token: string,
    objTypeCode?: string | undefined,
    location?: string,
    lang?: string,
    fileName?: string,
    mru?: string
  ) => Promise<AxiosResponse<Blob>>;
  getInvoiceAttachment: (
    invoiceServiceUrl: string,
    invoiceNumber: string,
    token: string,
    fileName: string | null,
    officialDocumentNumber: string | null,
    accountId: string,
    lang?: string
  ) => Promise<AxiosResponse<Blob>>;
  getDealerTimeLimitForInvoiceLineItem: (
    invoiceServiceUrl: string,
    accountId: string,
    transactionType: string,
    invoiceNumber: string,
    invoiceLineNumber: string,
    token: string,
    returnReason?: string,
    lang?: string
  ) => Promise<DealerTimeLimitInvoiceLineItem>;
  getInvoicesLineItemsAsExcel: (
    invoiceServiceUrl: string,
    accountId: string,
    invoiceNumber: string,
    token: string,
    invoiceType: string,
    dealerLocation: string,
    originalInvoiceNumber?: string,
    nfe?: string,
    originalNfe?: string
  ) => Promise<AxiosResponse<BlobResponse>>;
  getConsolidatedInvoicesLineItemsAsExcel: (
    invoiceServiceUrl: string,
    request: ConsolidatedInvoicesExcelRequest,
    token: string
  ) => Promise<AxiosResponse<BlobResponse>>;
  getInvoicesByDocumentNumber: (
    invoiceServiceUrl: string,
    accountId: string,
    token: string,
    documentNumber: string,
    invoiceType?: string,
    lang?: string
  ) => Promise<InvoicesResponse>;
};
